import React, { useState } from 'react';
import { Heart, MapPin, TrendingUp, Users, CheckCircle, ArrowRight, Building2, Briefcase, Target, Star, ChevronDown, Phone, Mail, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const budgetOptions = [
  'Meno di 10.000 EUR',
  '10.000 - 25.000 EUR',
  '25.000 - 50.000 EUR',
  '50.000 - 100.000 EUR',
  'Oltre 100.000 EUR',
];

const regions = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
  'Friuli Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia',
  'Toscana', 'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
];

const Affiliati = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    region: '',
    investment_budget: '',
    experience: '',
    motivation: '',
    has_location: false,
    location_details: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/db-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          action: 'insert',
          table: 'franchise_applications',
          data: formData,
        }),
      });

      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error?.message || json.error || 'Errore durante l\'invio della richiesta');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Si e\' verificato un errore. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: Building2,
      title: 'Brand Consolidato',
      description: 'Entra a far parte di un marchio riconosciuto nel settore degli eventi per single con una reputazione di eccellenza.',
    },
    {
      icon: Target,
      title: 'Modello Collaudato',
      description: 'Accedi a un sistema operativo testato e ottimizzato, con processi standardizzati per ogni aspetto del business.',
    },
    {
      icon: Users,
      title: 'Formazione Completa',
      description: 'Ricevi una formazione approfondita su organizzazione eventi, gestione clienti, marketing e tutti gli aspetti operativi.',
    },
    {
      icon: TrendingUp,
      title: 'Supporto Continuo',
      description: 'Assistenza costante dal team centrale: marketing, tecnologia, strategie commerciali e consulenza personalizzata.',
    },
    {
      icon: Briefcase,
      title: 'Tecnologia Avanzata',
      description: 'Utilizzo del nostro CRM proprietario, sistema di matching e piattaforma di gestione eventi all\'avanguardia.',
    },
    {
      icon: Star,
      title: 'Esclusiva Territoriale',
      description: 'Garanzia di esclusiva nella tua zona, per operare senza concorrenza interna e massimizzare il tuo potenziale.',
    },
  ];

  const steps = [
    { number: '01', title: 'Candidatura', description: 'Compila il modulo di richiesta con le tue informazioni e motivazioni.' },
    { number: '02', title: 'Valutazione', description: 'Il nostro team esamina la tua candidatura e il mercato della tua zona.' },
    { number: '03', title: 'Colloquio', description: 'Incontro conoscitivo per approfondire la collaborazione e rispondere alle tue domande.' },
    { number: '04', title: 'Avvio', description: 'Firma dell\'accordo, formazione iniziale e lancio della tua attivita\' FlashCrush.' },
  ];

  return (
    <div className="min-h-screen bg-[#FFF5F7]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#D91C5C] to-[#8B1538] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Heart className="h-6 w-6 text-white fill-white" />
              <span className="text-xl font-bold text-white">FlashCrush</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-white/80 hover:text-white transition-colors text-sm font-medium"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 px-4 bg-gradient-to-b from-[#FFF5F7] to-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#D91C5C]/10 text-[#D91C5C] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Building2 className="h-4 w-4" />
            Programma Affiliazione in Franchising
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Apri il Tuo Centro<br />
            <span className="text-[#D91C5C]">FlashCrush</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
            Diventa protagonista del settore degli eventi per single nella tua citta'.
            Un'opportunita' imprenditoriale unica con un brand consolidato,
            un modello di business collaudato e il supporto costante del nostro team.
          </p>
          <button
            onClick={() => {
              document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all inline-flex items-center gap-2 shadow-lg shadow-[#D91C5C]/25"
          >
            Richiedi Informazioni
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '500+', label: 'Eventi Organizzati' },
            { value: '10.000+', label: 'Match Creati' },
            { value: '15+', label: 'Citta\' Attive' },
            { value: '98%', label: 'Soddisfazione Clienti' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-[#D91C5C]">{stat.value}</div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perche' Diventare Affiliato FlashCrush
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Un pacchetto completo per avviare e far crescere la tua attivita' nel mondo degli eventi per single.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md hover:border-[#D91C5C]/20 transition-all group">
                <div className="w-14 h-14 bg-[#D91C5C]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#D91C5C]/20 transition-colors">
                  <b.icon className="h-7 w-7 text-[#D91C5C]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{b.title}</h3>
                <p className="text-gray-600 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Come Funziona
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Quattro semplici passi per iniziare il tuo percorso con FlashCrush.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#D91C5C]/10 mb-2">{step.number}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 text-[#D91C5C]/30">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="application-form" className="py-20 px-4 bg-gradient-to-b from-[#FFF5F7] to-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Richiedi l'Affiliazione
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Compila il modulo qui sotto e il nostro team ti contattera' entro 48 ore per discutere l'opportunita'.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Richiesta Inviata!</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Grazie per il tuo interesse nel programma di affiliazione FlashCrush.
                Il nostro team ti contattera' entro 48 ore lavorative per discutere i dettagli.
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-[#D91C5C] hover:bg-[#C01850] text-white px-6 py-3 rounded-xl font-semibold transition-all"
              >
                Torna alla Home
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <X className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-6">
                {/* Personal info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#D91C5C]" />
                    Dati Personali
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome *</label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        value={formData.full_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors"
                        placeholder="mario@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors"
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#D91C5C]" />
                    Zona di Interesse
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Citta' *</label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors"
                        placeholder="Milano"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Regione</label>
                      <select
                        name="region"
                        value={formData.region}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors"
                      >
                        <option value="">Seleziona regione</option>
                        {regions.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Business */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-[#D91C5C]" />
                    Informazioni sul Business
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Budget di Investimento</label>
                      <select
                        name="investment_budget"
                        value={formData.investment_budget}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors"
                      >
                        <option value="">Seleziona una fascia</option>
                        {budgetOptions.map(b => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Esperienza nel Settore</label>
                      <textarea
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors resize-none"
                        placeholder="Descrivi brevemente la tua esperienza nel settore eventi, ristorazione o attivita' imprenditoriali..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Motivazione</label>
                      <textarea
                        name="motivation"
                        value={formData.motivation}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors resize-none"
                        placeholder="Perche' vuoi diventare affiliato FlashCrush?"
                      />
                    </div>

                    <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
                      <input
                        type="checkbox"
                        name="has_location"
                        checked={formData.has_location}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 text-[#D91C5C] border-gray-300 rounded focus:ring-[#D91C5C]"
                      />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Ho gia' un locale / spazio disponibile
                        </label>
                        {formData.has_location && (
                          <textarea
                            name="location_details"
                            value={formData.location_details}
                            onChange={handleChange}
                            rows={2}
                            className="mt-3 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] transition-colors resize-none"
                            placeholder="Descrivi il locale: indirizzo, capienza, tipologia..."
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#D91C5C] hover:bg-[#C01850] text-white py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        Invia Richiesta di Affiliazione
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Inviando questo modulo acconsenti al trattamento dei tuoi dati personali secondo la nostra Privacy Policy.
                  </p>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#A81444] text-white py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="h-5 w-5 fill-white" />
            <span className="text-lg font-bold">FlashCrush</span>
          </div>
          <p className="text-white/80 text-sm">&copy; 2026 FlashCrush. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
};

export default Affiliati;
